import type { TedIntonationSpan, TedEmotion } from "@/types/screenplay";
import { createTedIntonationSpan } from "./tedIntonation";
import { isInlineMarker } from './inlineMarkers';
import { editorDirectionStyle, hasDeliveryDirection, normalizePauseDirection, type EditorGeminiDirection as GeminiDirection } from "./pauseDirections";

export const DELIVERY_SELECTION_CONFLICT = "Select a complete cue or pause marker, or select the surrounding text without cutting through a marker.";

/** Half-open intervals: touching boundaries are not overlaps; inspection never edits them. */
export function overlappingDeliveryRanges(spans: TedIntonationSpan[], start: number, end: number) {
  return spans.filter(span => span.start < end && span.end > start);
}

export function resolveDeliverySelection(text: string, spans: TedIntonationSpan[], start: number, end: number) {
  const valid = spans.filter(span => span.start >= 0 && span.end <= text.length && span.end > span.start &&
    span.selectedText === text.slice(span.start, span.end));
  if (start === end) {
    // Inspect the passage without changing the native caret (typing must not replace it).
    const span = valid.find(span => span.start <= start && start < span.end);
    return span ? { start: span.start, end: span.end, text: text.slice(span.start, span.end), blocked: false } : null;
  }
  while (start < end && /\s/u.test(text[start])) start += 1;
  while (end > start && /\s/u.test(text[end - 1])) end -= 1;
  if (end <= start) return null;
  return { start, end, text: text.slice(start, end), blocked: overlappingDeliveryRanges(valid, start, end)
    .some(span => isInlineMarker(span) && (start > span.start || end < span.end)) };
}

/** Values shared by the entire selection; differing layers stay unset rather
 * than borrowing the first passage's settings and overwriting its neighbours. */
export function commonDeliveryDirection(spans: TedIntonationSpan[], start: number, end: number): GeminiDirection {
  const ranges = overlappingDeliveryRanges(spans, start, end).sort((a,b)=>a.start-b.start);
  const directions: GeminiDirection[] = []; let cursor=start;
  for(const span of ranges) {
    if(span.start>cursor)directions.push({});
    if(!span.gemini?.pauseMarker)directions.push(savedDeliveryDirection(span)??{});
    cursor=Math.max(cursor,span.end);
  }
  if(cursor<end)directions.push({});
  return commonDirectionValues(directions);
}

export function commonDirectionValues(directions: GeminiDirection[]): GeminiDirection {
  const common={...directions[0]};
  for(const key of Object.keys(common) as (keyof GeminiDirection)[]) {
    if(!directions.every(d=>d[key]===common[key]))delete common[key];
  }
  return common;
}

/** Patch only explicitly chosen layers, splitting at existing boundaries.
 * Pauses and untouched text are retained; cues are not duplicated by splits. */
export function patchDeliveryRange(spans: TedIntonationSpan[], start: number, end: number, text: string, patch: GeminiDirection): TedIntonationSpan[] {
  const intersected=overlappingDeliveryRanges(spans,start,end);
  const edges=[...new Set([start,end,...intersected.flatMap(s=>[Math.max(start,s.start),Math.min(end,s.end)])])].sort((a,b)=>a-b);
  const updated=removeDeliveryRange(spans,start,end,text);
  for(let n=0;n<edges.length-1;n++) {
    const a=edges[n],b=edges[n+1],source=intersected.find(s=>s.start<=a&&s.end>=b);
    if(source?.gemini?.pauseMarker) {updated.push(source);continue;}
    if(source?.gemini?.cueMarker) {
      if(a!==source.start||b!==source.end)throw new Error(DELIVERY_SELECTION_CONFLICT);
      const cuePatch=Object.fromEntries(['volume','audioGainDb','volumeMotion','audioVolumeGroup','audioSpeed'].filter(key=>key in patch).map(key=>[key,patch[key as keyof GeminiDirection]]));
      updated.push({...source,gemini:{...source.gemini,...cuePatch}});continue;
    }
    const base={...(savedDeliveryDirection(source)??{})};
    if(source&&a>source.start){delete base.cue;delete base.pauseMs;}
    const next={...base,...patch};
    if('cue' in patch&&a>start)delete next.cue;
    if('pauseMs' in patch&&a>start)delete next.pauseMs;
    if(!hasDeliveryDirection(next))continue;
    const span=createGeminiSpan(a,b,text,next);
    updated.push(source?{...source,id:span.id,start:a,end:b,selectedText:span.selectedText,emotion:span.emotion,
      emotionDescription:span.emotionDescription,gemini:span.gemini,...(a>source.start?{pauseBeforeMs:0}:{})}:span);
  }
  return mergeAdjacentDeliveryRanges(updated,text);
}

/** Coalesce identical authored segments after an edit, never on load/inspection.
 * Preserve every event/marker boundary and every legacy synthesis setting. */
export function mergeAdjacentDeliveryRanges(spans:TedIntonationSpan[],text:string):TedIntonationSpan[] {
  const merged:TedIntonationSpan[]=[];
  const signature=(span:TedIntonationSpan)=>{
    const d=span.gemini;
    if(!d||isInlineMarker(span)||d.cue||d.pauseMs||span.pauseBeforeMs)return null;
    const keys=(Object.keys(d) as (keyof GeminiDirection)[]).filter(key=>d[key]!==undefined).sort();
    return JSON.stringify([span.emotion,span.emotionDescription,span.emotionStrength,span.applicationMode,
      span.pitchSemitones,span.volumeDb,keys.map(key=>[key,d[key]])]);
  };
  for(const span of [...spans].sort((a,b)=>a.start-b.start)) {
    const last=merged.at(-1),style=signature(span);
    if(last&&last.end===span.start&&style!==null&&style===signature(last)&&
      last.selectedText===text.slice(last.start,last.end)&&span.selectedText===text.slice(span.start,span.end)) {
      merged[merged.length-1]={...last,end:span.end,selectedText:text.slice(last.start,span.end)};
    }else merged.push(span);
  }
  return merged;
}

export function savedDeliveryDirection(span?: TedIntonationSpan): GeminiDirection | undefined {
  if (!span) return undefined;
  if (span.gemini) return normalizePauseDirection(span.gemini);
  const emotions = { neutral: "Neutral", angry: "Angry", happy: "Happy", sad: "Sad", fear: "Afraid", disgust: "Disgusted", surprise: "Surprised" };
  return { emotion: emotions[span.emotion] };
}

/** Replace only the selected interval, retaining either side of intersected ranges. */
export function removeDeliveryRange(spans: TedIntonationSpan[], start: number, end: number, text: string): TedIntonationSpan[] {
  return spans.flatMap(span => {
    if (span.end <= start || span.start >= end) return [span];
    const pieces: TedIntonationSpan[] = [];
    if (span.start < start) pieces.push({ ...span, id: `${span.id}:left`, end: start, selectedText: text.slice(span.start, start) });
    if (span.end > end) {
      const { cue: _cue, pauseMs: _pause, ...remainingDirection } = span.gemini ?? {};
      pieces.push({ ...span, id: `${span.id}:right`, start: end, selectedText: text.slice(end, span.end), pauseBeforeMs: 0,
        // A cue belongs to the original start, not every piece created by editing.
        ...(span.gemini ? { gemini: remainingDirection } : {}) });
    }
    return pieces;
  });
}
/** Clearing a style never deletes a structural pause or vocal-event marker. */
export function clearDeliveryRanges(spans:TedIntonationSpan[],text:string,start=0,end=text.length):TedIntonationSpan[] {
  const markers=spans.filter(isInlineMarker).map(span=>{
    if(span.end<=start||span.start>=end||span.gemini?.pauseMarker)return span;
    if(start>span.start||end<span.end)throw new Error(DELIVERY_SELECTION_CONFLICT);
    return {...span,emotion:'neutral' as const,emotionDescription:'',emotionStrength:1,pitchSemitones:0,volumeDb:0,pauseBeforeMs:0,
      gemini:{cueMarker:true,cue:span.gemini?.cue,unspoken:true}};
  });
  return [...markers,...removeDeliveryRange(spans.filter(s=>!isInlineMarker(s)),start,end,text)].sort((a,b)=>a.start-b.start);
}
export function createGeminiSpan(start: number, end: number, text: string, direction: GeminiDirection): TedIntonationSpan {
  const emotions: Record<string, TedEmotion> = { Angry: "angry", Happy: "happy", Sad: "sad", Afraid: "fear", Disgusted: "disgust", Surprised: "surprise" };
  return createTedIntonationSpan({ id: crypto.randomUUID(), start, end, selectedText: text.slice(start, end),
    emotion: emotions[direction.emotion ?? ""] ?? "neutral", emotionDescription: editorDirectionStyle(direction), gemini: { ...direction } });
}
